import React from "react";
import {
  Box,
  CircularProgress,
  Container,
  LinearProgress,
  Paper,
  ThemeProvider,
  Typography,
  createTheme,
  Grid2 as Grid,
} from "@mui/material";
import { getScoreColor } from "../utils/helpers";

const theme = createTheme({
  palette: {
    primary: {
      main: "#B94A3E",
    },
    secondary: {
      main: "#E6C068",
    },
  },
});

function ScoreBar({ name, score, metricId, scrollIntoView }) {
  return (
    <Box
      sx={{
        border: "1px solid rgb(17, 17, 17)",
        borderRadius: '10px',
        p: 2,
        boxShadow: 3,
        cursor: "pointer",
        boxShadow: 'rgb(17, 17, 19) -4px 4px 0px 0px',
      }}
      onClick={() => scrollIntoView(metricId)}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="body1" fontWeight="bold">
          {name}
        </Typography>
        <Typography variant="body1" fontWeight="medium">
          {score}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={score}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: "#E0E0E0",
          "& .MuiLinearProgress-bar": {
            backgroundColor: getScoreColor(score),
          },
        }}
      />
    </Box>
  );
}

function ReportGrade({
  domain,
  metrics,
  score,
  summary,
  metricIdMap,
  scrollIntoView,
}) {
  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box
          sx={{
            borderLeft: "4px solid black",
            pl: 2,
            mb: { xs: 4, md: 6 },
            py: 0.5,
          }}
          className="report-header"
        >
          <Typography variant="h5" sx={{ color: "#666" }}>
            Website Audit Report:
          </Typography>
          <Typography variant="h6" sx={{ color: "#666" }}>
            {domain}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 4,
            alignItems: "center",
            padding: 4,
            mb: 4,
            border: "1px solid rgb(17, 17, 17)",
            borderRadius: "25px",
            boxShadow: "rgb(17, 17, 19) -4px 4px 0px 0px",
          }}
        >
          <Box sx={{ position: "relative", display: "inline-flex", mr: 3 }}>
            <CircularProgress
              variant="determinate"
              value={score}
              size={120}
              thickness={4}
              sx={{
                color: getScoreColor(score),
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="h4"
                fontWeight="bold"
                color={getScoreColor(score)}
              >
                {score}%
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography
              variant="h2"
              sx={{ mb: 2, fontSize: { xs: "2rem", sm: "3rem" }, fontWeight: "550" }}
            >
              Summary:
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {summary}
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={4} sx={{ py: 4 }}>
          {metrics.map((metric) => (
            <Grid size={{ xs: 12, sm: 6 }}>
              <ScoreBar
                key={metric.category_name}
                name={metric.category_name}
                score={metric.category_score}
                metricId={metricIdMap[metric.category_name]}
                scrollIntoView={scrollIntoView}
                className="score-bar"
              />
            </Grid>
          ))}
        </Grid>

        <Box sx={{ my: 4 }} id="impact-rating">
          <Typography variant="h6" gutterBottom>
            Impact Rating:
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Our audit factors range from high impact to low impact based on how
            much they can influence the way your website converts visitors into
            leads and customers, performance, security and your rankings in
            search engine results.
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Apply the recommendations below to help your site look nicer, run
            faster, rank better and start converting more visitors into
            customers.
          </Typography>
        </Box>
      </Container>
    </ThemeProvider>
  );
}

export default ReportGrade;
